import {
  authSessionViewSchema,
  currentUserSchema,
  loginResponseSchema,
  totpEnrollResponseSchema,
  type ChangePasswordRequest,
  type CurrentUser,
  type LoginRequest,
  type LoginResponse,
  type TotpEnrollResponse,
} from '@kraaapf/contracts';
import { z } from 'zod';

import type { PortalClient } from '../http-client';

/**
 * Anmeldung und eigenes Konto.
 *
 * In keiner dieser Antworten steht ein Token — sie stehen in Cookies, die kein
 * Skript lesen kann. Deshalb gibt es hier auch nichts zu speichern: Wer
 * angemeldet ist, erfährt die Oberfläche über `me()`.
 */
function buildAuthApi(client: PortalClient) {
  return {
    /**
     * Meldet an.
     *
     * `skipRefresh`: Nach einem gescheiterten Anmeldeversuch wäre eine
     * Auffrischung eine zweite Ablehnung — und ein zweiter Eintrag im
     * Prüfprotokoll, der nach einem zweiten Versuch aussähe.
     */
    async login(input: LoginRequest): Promise<LoginResponse> {
      return client.post('/auth/login', loginResponseSchema, input, { skipRefresh: true });
    },

    async logout(): Promise<void> {
      await client.send('POST', '/auth/logout');
    },

    /**
     * Das angemeldete Konto.
     *
     * Der erste Aufruf beim Start der Oberfläche: Er beantwortet „bin ich
     * angemeldet?" und liefert dabei gleich alles, was die Kopfzeile braucht.
     */
    async me(): Promise<CurrentUser> {
      return client.get('/auth/me', currentUserSchema);
    },

    async sessions() {
      return client.get('/auth/sessions', z.object({ sessions: z.array(authSessionViewSchema) }));
    },

    async revokeSession(sessionId: string): Promise<void> {
      await client.send('DELETE', `/auth/sessions/${sessionId}`);
    },

    async enrollTotp(): Promise<TotpEnrollResponse> {
      return client.post('/auth/totp/enroll', totpEnrollResponseSchema);
    },

    async confirmTotp(code: string): Promise<void> {
      await client.send('POST', '/auth/totp/confirm', { code });
    },

    async disableTotp(password: string): Promise<void> {
      await client.send('POST', '/auth/totp/disable', { password });
    },

    async changePassword(input: ChangePasswordRequest): Promise<void> {
      await client.send('POST', '/auth/password', input);
    },
  };
}

/** Die öffentliche Form dieses Bereichs — abgeleitet, damit sie nicht zweimal dasteht. */
export type AuthApi = ReturnType<typeof buildAuthApi>;

export function createAuthApi(client: PortalClient): AuthApi {
  return buildAuthApi(client);
}
